import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queueService } from '@/services/queue.service';
import { queueApiService } from '@/services/queueApi.service';
import { CustomFieldConfig } from '@/components/queue/CustomFieldsBuilder';
import { toast } from '@/components/ui/use-toast';
import { z } from 'zod';

// Hook for fetching custom fields configuration
export const useCustomFieldsConfig = (queueId: string) => {
  console.log('[useCustomFieldsConfig] Hook called with queueId:', queueId);

  const result = useQuery({
    queryKey: ['event-custom-fields-v2', queueId], // Fixed key without timestamp
    queryFn: async () => {
      console.log('[useCustomFieldsConfig] queryFn executing for queueId:', queueId);

      if (!queueId) {
        console.log('[useCustomFieldsConfig] No queueId, returning empty array');
        return [];
      }

      try {
        // First get the queue to find the eventId (using public endpoint)
        console.log('[useCustomFieldsConfig] Fetching queue details from public endpoint...');
        const queue = await queueApiService.getPublicQueueInfo(queueId);
        console.log('[useCustomFieldsConfig] Queue response:', queue);

        if (!queue?.eventId) {
          console.log('[useCustomFieldsConfig] No eventId in queue, returning empty array');
          return [];
        }

        // Then get the custom fields from the event
        console.log('[useCustomFieldsConfig] Fetching custom fields for eventId:', queue.eventId);
        const fields = await queueApiService.getEventCustomFields(queue.eventId);
        console.log('[useCustomFieldsConfig] Custom fields response:', fields);

        return fields || [];
      } catch (error) {
        console.error('[useCustomFieldsConfig] Error fetching custom fields:', error);
        return [];
      }
    },
    enabled: !!queueId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: false, // Don't retry to avoid loops
  });

  console.log('[useCustomFieldsConfig] Query result:', {
    status: result.status,
    isLoading: result.isLoading,
    isError: result.isError,
    data: result.data
  });

  return result;
};

// Hook for updating custom fields configuration
export const useUpdateCustomFields = (queueId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customFields: CustomFieldConfig[]) =>
      queueService.updateCustomFieldsConfig(queueId, customFields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-custom-fields', queueId] });
      queryClient.invalidateQueries({ queryKey: ['queue', queueId] });
      toast({
        title: 'Campos atualizados',
        description: 'A configuração dos campos personalizados foi salva com sucesso.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao salvar',
        description: error.response?.data?.message || 'Erro ao salvar campos personalizados',
        variant: 'destructive',
      });
    },
  });
};

// Hook for building dynamic Zod schema from custom fields configuration
export const useDynamicSchema = (customFields: CustomFieldConfig[]) => {
  const buildSchema = useCallback(() => {
    const schemaShape: Record<string, z.ZodTypeAny> = {
      // Default required fields
      name: z.string().min(1, 'Nome é obrigatório'),
      phone: z.string().min(10, 'Telefone é obrigatório'),
      email: z.union([z.string().email('Email inválido'), z.literal('')]).optional(),
    };

    // Build the customFields object schema
    const customFieldsShape: Record<string, z.ZodTypeAny> = {};

    // Add custom fields to schema
    customFields.forEach((field) => {
      let fieldSchema: z.ZodTypeAny;

      switch (field.fieldType) {
        case 'text':
        case 'textarea':
          fieldSchema = z.string();

          // Apply validations
          if (field.validations) {
            field.validations.forEach((validation) => {
              switch (validation.type) {
                case 'minLength':
                  fieldSchema = (fieldSchema as z.ZodString).min(
                    Number(validation.value),
                    validation.errorMessage
                  );
                  break;
                case 'maxLength':
                  fieldSchema = (fieldSchema as z.ZodString).max(
                    Number(validation.value),
                    validation.errorMessage
                  );
                  break;
                case 'regex':
                  fieldSchema = (fieldSchema as z.ZodString).regex(
                    new RegExp(validation.value),
                    validation.errorMessage
                  );
                  break;
              }
            });
          }
          break;

        case 'number':
          fieldSchema = z.coerce.number();

          // Apply validations
          if (field.validations) {
            field.validations.forEach((validation) => {
              switch (validation.type) {
                case 'min':
                  fieldSchema = (fieldSchema as z.ZodNumber).min(
                    Number(validation.value),
                    validation.errorMessage
                  );
                  break;
                case 'max':
                  fieldSchema = (fieldSchema as z.ZodNumber).max(
                    Number(validation.value),
                    validation.errorMessage
                  );
                  break;
              }
            });
          }
          break;

        case 'date':
          fieldSchema = z.string().refine(
            (val) => {
              // Allow empty string or valid date
              if (!val || val === '') return !field.required; // Empty is ok if not required
              return !isNaN(Date.parse(val));
            },
            { message: 'Data inválida' }
          );

          // Apply age validations
          if (field.validations) {
            field.validations.forEach((validation) => {
              if (validation.type === 'minAge' || validation.type === 'maxAge') {
                fieldSchema = (fieldSchema as z.ZodTypeAny).refine(
                  (val) => {
                    // Skip age validation if date is empty and field is not required
                    if (!val || val === '') return !field.required;

                    const birthDate = new Date(val as string);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();

                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                      age--;
                    }

                    if (validation.type === 'minAge') {
                      return age >= Number(validation.value);
                    } else {
                      return age <= Number(validation.value);
                    }
                  },
                  { message: validation.errorMessage }
                );
              }
            });
          }
          break;

        case 'checkbox':
          fieldSchema = z.boolean();
          break;

        case 'select':
          if (field.options && field.options.length > 0) {
            fieldSchema = z.enum(field.options as [string, ...string[]]);
          } else {
            fieldSchema = z.string();
          }
          break;

        default:
          fieldSchema = z.string();
      }

      // Fields with showWhen are always optional in the Zod schema
      // (backend handles validation for visible fields - see Story 4-2)
      if (field.showWhen) {
        fieldSchema = fieldSchema.optional();
      } else if (!field.required) {
        fieldSchema = fieldSchema.optional();
      }

      // Add to customFields shape instead of root shape
      customFieldsShape[field.fieldKey] = fieldSchema;

      // If field has a repeatGroup, add the group array to the schema
      if (field.repeatGroup) {
        customFieldsShape[field.repeatGroup.groupKey] = z.array(z.record(z.string(), z.any())).optional();
      }
    });

    // Add the customFields object to the main schema
    if (Object.keys(customFieldsShape).length > 0) {
      schemaShape.customFields = z.object(customFieldsShape);
    }

    return z.object(schemaShape);
  }, [customFields]);

  return buildSchema();
};

// Hook for validating custom field data
export const useValidateCustomFields = () => {
  const validateField = useCallback((
    value: any,
    field: CustomFieldConfig
  ): { isValid: boolean; error?: string } => {
    try {
      // Type validation
      switch (field.fieldType) {
        case 'text':
        case 'textarea':
          if (typeof value !== 'string') {
            return { isValid: false, error: `${field.label} deve ser um texto` };
          }
          break;
        case 'number':
          if (typeof value !== 'number' && !Number.isFinite(Number(value))) {
            return { isValid: false, error: `${field.label} deve ser um número` };
          }
          break;
        case 'date':
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return { isValid: false, error: `${field.label} deve ser uma data válida` };
          }
          break;
        case 'checkbox':
          if (typeof value !== 'boolean') {
            return { isValid: false, error: `${field.label} deve ser verdadeiro ou falso` };
          }
          break;
        case 'select':
          if (field.options && !field.options.includes(value)) {
            return { isValid: false, error: `${field.label} deve ser uma das opções válidas` };
          }
          break;
      }

      // Custom validations
      if (field.validations) {
        for (const validation of field.validations) {
          switch (validation.type) {
            case 'min':
              if (field.fieldType === 'number' && Number(value) < Number(validation.value)) {
                return { isValid: false, error: validation.errorMessage };
              }
              break;
            case 'max':
              if (field.fieldType === 'number' && Number(value) > Number(validation.value)) {
                return { isValid: false, error: validation.errorMessage };
              }
              break;
            case 'minLength':
              if (typeof value === 'string' && value.length < Number(validation.value)) {
                return { isValid: false, error: validation.errorMessage };
              }
              break;
            case 'maxLength':
              if (typeof value === 'string' && value.length > Number(validation.value)) {
                return { isValid: false, error: validation.errorMessage };
              }
              break;
            case 'regex':
              if (typeof value === 'string' && !new RegExp(validation.value).test(value)) {
                return { isValid: false, error: validation.errorMessage };
              }
              break;
          }
        }
      }

      return { isValid: true };
    } catch (error) {
      return { isValid: false, error: 'Erro na validação do campo' };
    }
  }, []);

  return { validateField };
};